import React, { useEffect, useState } from 'react';
import { Checkbox, Toggle, TooltipTrigger, Text, sparkToast, useReadOnly } from '@spark';
import { data_from_server_post } from '@/shared/http/client';
import { TaskType } from '@/shared/domain/taskTypes';
import { useAuth } from '@/features/auth/AuthContext';
import AnalysisPropertyRow from '@/features/datasets/components/DataCollection/dataOverview/sections/AnalysisPropertyRow';

const PUNCTUATION_PERCENTAGE_THRESHOLD = 50;

/**
 * "Punctuation detected" panel for NER tasks.
 *
 * Auto-detects whether any entity has > 50% punctuation occurrences or honors
 * a manual user override (`is_punctuation_detected`). Persists the toggle via
 * `update-dataset-punctuation-status`.
 */
const PunctuationSection = ({
    currentUsecase,
    entitiesCounts,
    selectedTaskType,
    isPunctuationDetected,
    isBenchmark = false,
    onPunctuationChange,
}) => {
    const { role } = useAuth();
    const { readOnly } = useReadOnly();
    const [checked, setChecked] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const isViewer = role === 'Viewer';

    const shouldShow =
        selectedTaskType === TaskType.NER || selectedTaskType === TaskType.NER_WITH_RELATIONS;

    useEffect(() => {
        if (!shouldShow) return;
        setInitialized(false);
    }, [shouldShow, isBenchmark]);

    useEffect(() => {
        if (!shouldShow || initialized) return;
        try {
            if (isPunctuationDetected === true) {
                setChecked(true);
                setInitialized(true);
                return;
            }
            if (isPunctuationDetected === false) {
                setChecked(false);
                setInitialized(true);
                return;
            }
            const entityEntries = Object.entries(entitiesCounts || {});
            let hasEntityWithHighPunctuation = false;
            for (const [, entityData] of entityEntries) {
                const total = entityData?.total || 0;
                const hasPunct = entityData?.has_punctuation || 0;
                if (total > 0) {
                    const percentage = (hasPunct / total) * 100;
                    if (percentage >= PUNCTUATION_PERCENTAGE_THRESHOLD) {
                        hasEntityWithHighPunctuation = true;
                        break;
                    }
                }
            }
            setChecked(hasEntityWithHighPunctuation);
            setInitialized(true);
        } catch (e) {
            setChecked(false);
            setInitialized(true);
        }
    }, [shouldShow, entitiesCounts, isPunctuationDetected, initialized, isBenchmark]);

    if (!shouldShow) return null;

    const handleChange = (newChecked) => {
        if (!currentUsecase) return;
        setChecked(newChecked);
        if (onPunctuationChange) onPunctuationChange(newChecked, isBenchmark);
        const datasets = isBenchmark ? currentUsecase.benchmarks : currentUsecase.datasets;
        data_from_server_post(
            'update-dataset-punctuation-status',
            {
                client_id: currentUsecase.client_id,
                datasets,
                is_benchmark: isBenchmark,
                is_punctuation_detected: newChecked,
                llm_usecase_id: currentUsecase._id,
            },
            (response) => {
                if (!response?.success) {
                    setChecked(!newChecked);
                    if (onPunctuationChange) onPunctuationChange(!newChecked, isBenchmark);
                    sparkToast.error(response?.message || 'Failed to update punctuation status');
                }
            },
            (error) => {
                setChecked(!newChecked);
                if (onPunctuationChange) onPunctuationChange(!newChecked, isBenchmark);
                sparkToast.error(typeof error === 'string' ? error : 'Failed to update punctuation status');
            }
        );
    };

    if (checked) {
        return (
            <AnalysisPropertyRow
                icon="circle-info"
                label="Punctuation detected"
                description={(
                    <TooltipTrigger
                        text="We will not remove punctuation from detected entity content."
                        placement="top"
                    >
                        <Text as="span" variant="detail-regular" color="secondary">
                            Entities include punctuation
                        </Text>
                    </TooltipTrigger>
                )}
                trailing={(
                    <Toggle
                        size="small"
                        active={checked}
                        disabled={readOnly || isViewer}
                        onChange={(e) => handleChange(e.target.checked)}
                    />
                )}
            />
        );
    }

    return (
        <AnalysisPropertyRow
            icon="circle-info"
            label="Punctuation detected"
            value="No punctuation detecteds"
        >
            <Checkbox
                className="data-overview-detail-checkbox"
                label="Check this box if entities contain punctuation"
                status="inactive"
                disabled={readOnly || isViewer}
                onChange={(e) => handleChange(e.target.checked)}
            />
        </AnalysisPropertyRow>
    );
};

export default PunctuationSection;
